import React, { useState, useEffect } from 'react';
import config from '../config';
import { CPC_DEFINITIONS } from '../constants';

const EmptyResultsMessage = ({
  loading,
  hasSearched,
  results,
  query,
  transparency,
  onCpcClick
}) => {
  const [relatedCpc, setRelatedCpc] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    // Check if query looks like a CPC code (starts with letter, has numbers)
    const isCpcCode = query && /^[A-H]\d{2}/i.test(query.trim());
    
    if (isCpcCode && hasSearched && results && results.length === 0) {
      setLoadingRelated(true);
      fetch(`${config.API_URL}/api/related-cpc?cpc_code=${encodeURIComponent(query)}`)
        .then(res => res.json())
        .then(data => {
          setRelatedCpc(data);
          setLoadingRelated(false);
        })
        .catch(err => {
          console.error('Error fetching related CPC:', err);
          setLoadingRelated(false);
        });
    } else {
      setRelatedCpc(null);
    }
  }, [query, hasSearched, results]);

  if (loading || !hasSearched || !results || results.length !== 0 || !query || !transparency) {
    return null;
  }

  return (
    <div style={{
      padding: '32px',
      textAlign: 'center',
      backgroundColor: '#fff7ed',
      border: '2px solid #fed7aa',
      borderRadius: '12px',
      marginTop: '24px',
      maxWidth: '700px',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#9a3412', marginBottom: '16px' }}>
        No patents found for [{query}] in our current database
      </h3>
      
      {relatedCpc && relatedCpc.explanation && (
        <div style={{ 
          backgroundColor: '#fef3c7', 
          padding: '16px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          textAlign: 'left'
        }}>
          <p style={{ fontSize: '14px', color: '#92400e', marginBottom: '8px', fontWeight: '600' }}>
            📚 Understanding CPC Classification
          </p>
          <p style={{ fontSize: '14px', color: '#78350f', lineHeight: '1.6', margin: 0 }}>
            {relatedCpc.explanation}
          </p>
        </div>
      )}

      {loadingRelated && (
        <p style={{ color: '#7c2d12', fontSize: '14px' }}>Looking for related CPC codes...</p>
      )}

      {relatedCpc && relatedCpc.related_codes && relatedCpc.related_codes.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: '15px', color: '#9a3412', fontWeight: '600', marginBottom: '12px' }}>
            ✨ Related CPC codes in our database:
          </p>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px', 
            justifyContent: 'center',
            marginBottom: '12px'
          }}>
            {relatedCpc.related_codes.map((item, idx) => (
              <button
                key={idx}
                onClick={() => onCpcClick && onCpcClick(item.code)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#fff',
                  border: '2px solid #f97316',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#c2410c',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f97316';
                  e.target.style.color = '#fff';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#fff';
                  e.target.style.color = '#c2410c';
                }}
                title={`${item.count.toLocaleString()} patents - Click to search`}
              >
                {item.code} ({item.count.toLocaleString()})
              </button>
            ))}
          </div>
          <p style={{ fontSize: '12px', color: '#a16207', fontStyle: 'italic' }}>
            Click any code to search • Numbers show patents in our database
          </p>
        </div>
      )}

      {(!relatedCpc || !relatedCpc.related_codes || relatedCpc.related_codes.length === 0) && !loadingRelated && (
        <div style={{ fontSize: '15px', color: '#7c2d12', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <p style={{ fontWeight: '600', marginBottom: '8px' }}>What to try instead:</p>
          <ul style={{ listStyle: 'none', padding: 0, lineHeight: '1.8' }}>
            <li>• Search using descriptive keywords related to {CPC_DEFINITIONS[query] ? CPC_DEFINITIONS[query].toLowerCase() : 'this technology area'}</li>
            <li>• Try related CPC codes from your previous search results</li>
          </ul>
        </div>
      )}
      
      <p style={{ fontSize: '13px', color: '#a16207', marginTop: '16px', fontStyle: 'italic' }}>
        💡 Our database is continuously growing. This CPC code may be available in future updates.
      </p>
    </div>
  );
};

export default EmptyResultsMessage;